import React from 'react';
import { Plus, Minus } from 'lucide-react';
import BottomSheet from '../BottomSheet';
import { config } from '../../config/environment';

interface Produto {
  id: number;
  nome: string;
  descricao: string;
  preco: number;
  imagem_url?: string;
  codigo?: string;
  venda_por_peso?: boolean | number;
}

interface MobileProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  produto: Produto | null;
  quantidade: number;
  observacoes: string;
  onQuantidadeChange: (qtd: number) => void;
  onObservacoesChange: (obs: string) => void;
  peso: number | null;
  onPesoChange: (peso: number | null) => void;
  onAdicionar: () => void;
  onCancelar: () => void;
}

const MobileProductModal: React.FC<MobileProductModalProps> = ({
  isOpen,
  onClose,
  produto,
  quantidade,
  observacoes,
  onQuantidadeChange,
  onObservacoesChange,
  peso,
  onPesoChange,
  onAdicionar,
  onCancelar
}) => {
  if (!produto) return null;

  const isPeso = !!produto.venda_por_peso;
  const total = isPeso 
    ? (produto.preco * (peso || 0)) 
    : (produto.preco * quantidade);

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={produto.nome}
      maxHeight="100dvh"
      fullHeight={true}
    >
      <div className="flex flex-col h-full bg-[#111318]">
        {/* Imagem do Produto */}
        {produto.imagem_url && (
          <div className="px-4 pt-2">
            <div className="w-full h-48 sm:h-64 bg-white/5 rounded-3xl overflow-hidden relative group">
              <img
                src={produto.imagem_url.startsWith('http') 
                  ? produto.imagem_url 
                  : `${config.api.baseUrl || window.location.origin}${produto.imagem_url.startsWith('/') ? '' : '/'}${produto.imagem_url}`}
                alt={produto.nome}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60" />
              
              {produto.codigo && (
                <div className="absolute top-4 left-4">
                  <span className="bg-black/60 backdrop-blur-md text-white/90 text-[10px] px-3 py-1 rounded-full border border-white/10 font-bold uppercase tracking-wider">
                    SKU: {produto.codigo}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="p-6 space-y-8 flex-1">
          {/* Header Info (if no image or for better layout) */}
          {!produto.imagem_url && produto.codigo && (
            <div className="flex items-center gap-2">
              <span className="bg-orange-500/10 text-orange-400 text-[10px] px-3 py-1 rounded-full border border-orange-500/20 font-bold uppercase tracking-wider">
                Ref: {produto.codigo}
              </span>
            </div>
          )}

          {/* Descrição */}
          {produto.descricao && (
            <div className="space-y-2">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest px-1">Detalhes</h4>
              <p className="text-gray-400 text-sm leading-relaxed px-1">
                {produto.descricao}
              </p>
            </div>
          )}

          {/* Quantidade ou Peso */}
          <div className="space-y-4">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest px-1">
                {isPeso ? 'Peso (KG)' : 'Quantidade'}
              </h4>
            
            {isPeso ? (
              <div className="relative">
                <input
                  type="tel"
                  inputMode="numeric"
                  value={peso === null ? '' : peso.toFixed(3).replace('.', ',')}
                  onChange={(e) => {
                    // Remove tudo que não for dígito
                    const apenasNumeros = e.target.value.replace(/\D/g, '');
                    if (!apenasNumeros) {
                      onPesoChange(null);
                      return;
                    }
                    // Divide por 1000 para gerar as 3 casas decimais
                    const valorNum = parseInt(apenasNumeros, 10) / 1000;
                    onPesoChange(valorNum);
                  }}
                  placeholder="0,000"
                  autoFocus
                  className="w-full px-6 py-5 bg-white/5 border-2 border-orange-500/50 rounded-2xl text-white text-3xl font-black text-center placeholder-gray-700 focus:outline-none focus:border-orange-500 shadow-lg shadow-orange-500/10 transition-all"
                />
                <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none">
                  <span className="text-orange-500 font-black text-xl">KG</span>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-white/5 p-2 rounded-2xl border border-white/5">
                <button
                  onClick={() => onQuantidadeChange(Math.max(1, quantidade - 1))}
                  className="w-14 h-14 bg-white/5 rounded-xl flex items-center justify-center text-white hover:bg-white/10 transition-all active:scale-90"
                >
                  <Minus className="h-6 w-6 text-gray-400" />
                </button>
                
                <div className="flex flex-col items-center">
                  <span className="text-3xl font-black text-white leading-none">
                    {quantidade.toString().padStart(2, '0')}
                  </span>
                  <span className="text-[10px] text-gray-500 font-bold uppercase mt-1">unidades</span>
                </div>

                <button
                  onClick={() => onQuantidadeChange(quantidade + 1)}
                  className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center text-white hover:bg-orange-600 shadow-lg shadow-orange-500/20 transition-all active:scale-90"
                >
                  <Plus className="h-6 w-6" />
                </button>
              </div>
            )}
          </div>

          {/* Observações */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest px-1">Observações</h4>
            <div className="relative">
              <textarea
                value={observacoes}
                onChange={(e) => onObservacoesChange(e.target.value)}
                className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500/30 transition-all text-sm leading-relaxed resize-none"
                placeholder="Ex: Sem cebola, ponto da carne..."
                rows={3}
                style={{ fontSize: '16px' }}
              />
            </div>
          </div>
        </div>

        {/* Footer Action */}
        <div className="p-6 pt-2 pb-safe-offset-4 bg-[#111318] border-t border-white/5">
          <div className="flex items-center justify-between mb-6 px-1">
            <div>
              <p className="text-xs text-gray-500 font-bold uppercase">Preço Total</p>
              <p className="text-2xl font-black text-white">
                R$ {total.toFixed(2)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500 font-bold uppercase">Unitário / {isPeso ? 'KG' : 'Un'}</p>
              <p className="text-sm text-gray-400">R$ {produto.preco.toFixed(2)}</p>
            </div>
          </div>

          <button
            onClick={() => {
              // Garante que enviará null se for por unidade, ou ignorará qtd se for por peso
              // A limpeza final pode ser tratada no onAdicionar no pai.
              onAdicionar();
            }}
            disabled={isPeso && (!peso || peso <= 0)}
            className={`w-full py-5 rounded-2xl font-bold text-lg shadow-xl active:scale-[0.98] transition-all flex items-center justify-center gap-3 ${
              isPeso && (!peso || peso <= 0)
                ? 'bg-gray-700 text-gray-500 shadow-none grayscale cursor-not-allowed'
                : 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-orange-500/20'
            }`}
          >
            Confirmar Pedido
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default MobileProductModal;
